import { useFormik } from "formik";
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";
import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { View } from "react-native";
import { useNavigation, CommonActions } from "@react-navigation/native";
import { Dropdown } from "react-native-element-dropdown";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";

import { eventTypes, makeSlug } from "@/components/globalUtils";
import { createEvent } from "@/components/http/events";
import { CreateEventSchema } from "@/components/screens/Schemas";
import AppDatePicker from "@/components/shared/AppDatePicker";
import MultiDaySelector from "@/components/shared/MultiDaySelector";
import TimezonePicker from "@/components/shared/TimezonePicker";
import { notify } from "./AppToast";
import Button from "@/components/settings/Button";
import {
  Field,
  FieldLabel,
  TextField,
  TextInputBox,
} from "@/components/settings/Field";
import { RowGroup, ToggleRow } from "@/components/settings/SettingRow";
import { colors, control, radius, text } from "@/components/settings/theme";
import useWorkspaceAuth from "@/components/teams/useWorkspaceAuth";
import { useTeamAccess } from "@/components/teams/useWorkspace";
import { DONATIONS_ENABLED } from "@/components/donations/config";
import {
  readFundraiserIntent,
  updateFundraiserIntent,
} from "@/components/donations/fundraiserIntent";
import { waitForNonprofitWaiverClaim } from "@/components/donations/nonprofitWaiver";
import { t } from "@/localization";

// Schedule your event: title, link, type (personal accounts), date, timezone
// and days, then one "Schedule event" button (creating an event can use a
// credit, so it never saves on its own).
const CreateEventForm = (
  { fundraiserMode = false, funeralMode = false, initialTitle = "" },
  ref
) => {
  const navigation = useNavigation();
  // Teams: the account you work in (yours, or one shared with you).
  const { account_type, refetchProfile, isWorkspace } = useWorkspaceAuth();

  // Business accounts (incl. nonprofits) can opt in while creating the event;
  // individual accounts find Fundraising in the event menu. In someone else's
  // account it's the admins' (Teams).
  const team = useTeamAccess();
  const showFundraiserOption =
    DONATIONS_ENABLED &&
    (!isWorkspace || team.can("fundraising")) &&
    (fundraiserMode || account_type === "business");
  const [raiseDonations, setRaiseDonations] = useState(!!fundraiserMode);

  const f = useFormik({
    initialValues: {
      event_type: fundraiserMode
        ? "other"
        : funeralMode
        ? "funeral"
        : "wedding",
      event_timezone: moment.tz.guess(),
      event_datetime: null,
      days: 1,
      title: initialTitle || "",
      alias: initialTitle ? makeSlug(initialTitle) : "",
    },
    validationSchema: CreateEventSchema,
    onSubmit: async (values, actions) => {
      actions.setSubmitting(true);

      try {
        // A nonprofit's waiver claim must land first: the server unlocks
        // events when they are created. Bounded, never throws, instant when
        // there is nothing to claim.
        await waitForNonprofitWaiverClaim();

        // Create an event
        const event = await createEvent(values);

        // Refetch profile to get updated credits
        await refetchProfile();

        const routes = [
          { name: "EventList" }, // This will be at the bottom of the stack
          { name: "EventEdit", params: { slug: event.slug, event } }, // This will be on top
        ];

        if (showFundraiserOption && raiseDonations) {
          // Straight to fundraiser setup; the organization picked on the
          // nonprofit landing page is preselected there.
          const intent = await readFundraiserIntent();
          if (intent && (fundraiserMode || intent.stage === "captured")) {
            await updateFundraiserIntent({
              stage: "event_created",
              slug: event.slug,
              boundAt: Date.now(),
              prompted: true,
            });
          }
          routes.push({
            name: "EventDonations",
            params: { slug: event.slug, event },
          });
        } else if (!event.is_published && !isWorkspace) {
          // Only add PayWall if event is not published (buying is the
          // account holder's: members land on the event, which says so)
          routes.push({ name: "PayWall", params: { slug: event.slug, event } });
        }

        navigation.dispatch(
          CommonActions.reset({
            index: routes.length - 1,
            routes: routes,
          })
        );
      } catch (err) {
        actions.setSubmitting(false);
        notify.error(
          funeralMode
            ? t("Couldn't schedule the service")
            : t("Couldn't schedule the event"),
          String(err?.message || err)
        );
      }
    },
  });

  // The link follows the title until the user edits the link itself. (Not
  // Formik's `touched`: a submit attempt marks every field touched.)
  const aliasEdited = useRef(false);

  // The suggested fundraiser title can arrive after the first render (the
  // stored intent is read asynchronously); only fill an untouched, empty title.
  useEffect(() => {
    if (!initialTitle || f.values.title || f.touched.title) return;
    f.setFieldValue("title", initialTitle, false);
    if (!aliasEdited.current) {
      f.setFieldValue("alias", makeSlug(initialTitle), true);
    }
  }, [initialTitle]);

  useImperativeHandle(ref, () => ({
    isReadyToSave: () => {
      return !!f.dirty && !f.isSubmitting;
    },
  }));

  const updateTimeZone = useMemo(() => f.handleChange("event_timezone"), []);
  const showLink = aliasEdited.current || !!f.values.title || !!f.values.alias;
  // The link is filled in from the title, so its availability shows at once.
  const linkError = f.values.alias
    ? f.errors.alias
    : f.touched.alias || f.submitCount
    ? f.errors.alias
    : null;
  // Just the check mark in the field: a line of text under it would come and
  // go while the title, date or link change, and move the form.
  const linkOk = !!f.values.alias && !f.errors.alias;
  const showError = (name) =>
    f.touched[name] || f.submitCount ? f.errors[name] : null;

  return (
    <View>
      <TextField
        label={funeralMode ? t("Service title") : t("Event title")}
        help="create.title"
        value={f.values.title}
        onChangeText={(v) => {
          if (!aliasEdited.current) {
            f.setFieldValue("alias", makeSlug(v), true);
          }
          f.handleChange("title")(v);
        }}
        onBlur={f.handleBlur("title")}
        placeholder={
          funeralMode
            ? t("e.g. Celebration of Life for Jane Doe")
            : t("e.g. Anna & Mark's wedding")
        }
        onSubmitEditing={() => f.handleSubmit()}
        error={showError("title")}
      />

      {showLink && (
        <Field
          label={t("Event link")}
          help="create.eventLink"
          error={linkError}
        >
          <TextInputBox
            prefix="evt.live/"
            value={f.values.alias}
            onChangeText={(v) => {
              aliasEdited.current = true;
              f.handleChange("alias")(v);
            }}
            onBlur={f.handleBlur("alias")}
            placeholder={t("your-event-link")}
            accessibilityLabel={t("Event link")}
            autoCapitalize="none"
            autoCorrect={false}
            error={linkError}
            trailing={
              linkOk ? (
                <Ionicons
                  name="checkmark-circle"
                  size={20}
                  color={colors.success}
                  accessibilityLabel={t("Available")}
                />
              ) : null
            }
          />
        </Field>
      )}

      {account_type !== "business" && (
        <View style={tw`mb-4`}>
          <FieldLabel label={t("Event type")} help="create.eventType" />
          <Dropdown
            data={eventTypes.map((type) => ({
              name: type.label,
              value: type.value,
            }))}
            labelField="name"
            valueField="value"
            value={f.values.event_type}
            onChange={(item) => f.setFieldValue("event_type", item.value)}
            style={[
              tw`px-3.5 bg-white`,
              {
                height: control.lg,
                borderWidth: 1,
                borderColor: colors.border,
                borderRadius: radius.control,
              },
            ]}
            selectedTextStyle={text.body}
            itemTextStyle={text.body}
            containerStyle={tw`p-1 border border-gray-300 rounded-lg`}
            placeholder={t("Choose the type of event")}
            accessibilityLabel={t("Event type")}
          />
        </View>
      )}

      <View style={tw`mb-4`}>
        <FieldLabel label={t("Date and time")} help="create.dateTime" />
        <AppDatePicker
          selected={f.values.event_datetime}
          selectedValue={f.values.event_datetime}
          onChange={(value) => {
            f.setFieldValue("event_datetime", value);
          }}
          popperPlacement="top"
          onBlur={f.handleBlur("event_datetime")}
          placeholderText={
            funeralMode
              ? t("When the service starts")
              : t("When the event starts")
          }
          iconName="calendar-outline"
          errorMessage={showError("event_datetime")}
        />
        <TimezonePicker
          value={f.values.event_timezone}
          onChange={updateTimeZone}
          align="left"
          helpTopic="details.timezone"
        />
        <MultiDaySelector
          f={f}
          align="left"
          helpTopic="create.days"
          linkText={t("Make it a multi-day event")}
        />
      </View>

      {showFundraiserOption && (
        <RowGroup style={tw`mb-6`}>
          <ToggleRow
            label={t("Raise donations for a nonprofit")}
            description={t(
              "Viewers can donate on your event page. You'll choose the organization after the event is created."
            )}
            help="create.fundraiser"
            value={raiseDonations}
            onValueChange={setRaiseDonations}
            disabled={f.isSubmitting}
          />
        </RowGroup>
      )}

      <Button
        text={funeralMode ? t("Schedule service") : t("Schedule event")}
        variant="primary"
        icon="calendar"
        onPress={() => f.handleSubmit()}
        loading={f.isSubmitting}
        disabled={f.isSubmitting}
        style={tw`mt-2`}
      />
    </View>
  );
};

export default React.forwardRef(CreateEventForm);
